/**
 * Appels HTTP non authentifiés (login, register).
 * Utilise fetch brut — pas useFetch() qui nécessite un token existant.
 */
import { ApiError } from '../../lib/http/ApiError';
import type { ApiErrorPayload } from '../../lib/http/http.types';
import { DEFAULT_TENANT } from '../../shared/constants';
import type {
  AuthToken,
  LoginPayload,
  MeResponse,
  ResetPasswordPayload,
  ValidateResetTokenResponse,
} from './authTypes';

const BASE_URL = `${window.__env__?.VITE_BACKEND_HOMERE_CONNECT_DOMAIN ?? '/api'}`;

function toAuthError(text: string, status: number, statusText: string): ApiError {
  const payload: ApiErrorPayload = (() => {
    try {
      return JSON.parse(text) as ApiErrorPayload;
    } catch {
      return { message: text || statusText, statusCode: status };
    }
  })();
  return new ApiError(payload, status);
}

async function parseAuthResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!response.ok) throw toAuthError(text, response.status, response.statusText);
  return JSON.parse(text) as T;
}

/**
 * For content-free endpoints: resolves on any 2xx WITHOUT parsing the body
 * (a 204 / empty body must not be treated as a failure), throws on error.
 */
async function assertAuthOk(response: Response): Promise<void> {
  if (response.ok) return;
  const text = await response.text();
  throw toAuthError(text, response.status, response.statusText);
}

// TODO : Replace tenant-id header with domain-based tenant resolution when backend supports it (e.g. tenant1.homereconnect.fr → tenant-id = 'tenant1'). The tenant-id header is intentional for dev & test environments only and must be removed before going to production.
export const authApi = {
  login: (payload: LoginPayload): Promise<AuthToken> =>
    fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'tenant-id': DEFAULT_TENANT },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then(res => parseAuthResponse<AuthToken>(res)),

  logout: (token: string, options?: { signal?: AbortSignal }): Promise<{ message: string }> =>
    fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'tenant-id': DEFAULT_TENANT,
      },
      credentials: 'include',
      signal: options?.signal,
    }).then(res => parseAuthResponse<{ message: string }>(res)),

  // Role is deliberately absent from the login response — /auth/me is the sanctioned
  // way to read it (decoded JWT payload echoed back over HTTPS).
  me: (token: string): Promise<MeResponse> =>
    fetch(`${BASE_URL}/auth/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'tenant-id': DEFAULT_TENANT,
      },
      credentials: 'include',
    }).then(res => parseAuthResponse<MeResponse>(res)),

  // Appelé uniquement par le renouvellement d'AuthProvider, sous le verrou
  // AUTH_COOKIE_LOCK_NAME partagé avec le logout serveur (HOMCO-1269) : deux
  // /auth/refresh concurrents avec le même cookie purgent TOUTES les sessions de
  // l'utilisateur (HOMCO-1184).
  refresh: (options?: { signal?: AbortSignal }): Promise<{ access_token: string }> =>
    fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'tenant-id': DEFAULT_TENANT },
      credentials: 'include',
      signal: options?.signal,
    }).then(res => parseAuthResponse<{ access_token: string }>(res)),

  // Requests a password-reset link for the given email. The response is
  // intentionally content-free (anti-enumeration): the UI always shows the same
  // confirmation regardless of whether the account exists — so any 2xx resolves
  // without parsing a body (the endpoint may return 204 / empty).
  // Scaffold — the backend `/auth/forgot-password` endpoint is not implemented yet.
  requestPasswordReset: (email: string): Promise<void> =>
    fetch(`${BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'tenant-id': DEFAULT_TENANT },
      body: JSON.stringify({ email }),
      credentials: 'include',
    }).then(res => assertAuthOk(res)),

  // Silent check when the emailed reset link is opened (`?token=…`). Backend returns
  // 200 `{ valid: true }` for a usable token and 400 when invalid/expired — so a
  // non-2xx rejects here (caller treats reject OR `valid === false` as invalid).
  validateResetToken: (token: string): Promise<ValidateResetTokenResponse> =>
    fetch(`${BASE_URL}/auth/validate-reset-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'tenant-id': DEFAULT_TENANT },
      body: JSON.stringify({ token }),
      credentials: 'include',
    }).then(res => parseAuthResponse<ValidateResetTokenResponse>(res)),

  // Sets the new password from the reset token. Resolves on 200 (user can sign in
  // immediately); throws an ApiError on 400 (token invalid/expired, or — should the
  // front-end policy ever diverge — the server-side password policy).
  resetPassword: (payload: ResetPasswordPayload): Promise<{ message: string }> =>
    fetch(`${BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'tenant-id': DEFAULT_TENANT },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then(res => parseAuthResponse<{ message: string }>(res)),

  // Scaffold — à compléter quand le backend implémentera l'endpoint
  // register: (payload: RegisterPayload): Promise<AuthToken> => ...
};
